/* ============================================================
   component-load-more.js  (v135, Section Rendering API)

   Loads every product in a collection / search / blog listing as
   the shopper scrolls, with a "See more" button as a manual
   fallback. Uses Shopify's Section Rendering API
   (?section_id=...&page=N) to fetch each page. That endpoint
   reliably honors the page parameter and returns just the section
   markup, so we do not depend on the theme's alternate ?view=
   template (which was being redirected on this store and caused
   loading to stall after a few pages).

   Key points:
   - We track the page number ourselves and request the next one
     directly, so the chain never dead-ends on a missing
     data-next-url.
   - A hard in-flight lock guarantees one request at a time.
   - We stop only when a fetched page returns zero products.
   - The "You seen X out of Y" count is recomputed from the real
     number of product cards on the page and the real total.
   - Scroll position is restored after back navigation by replaying
     the pages the shopper had open.
   ============================================================ */
!(function () {
  if ('scrollRestoration' in history) {
    try { history.scrollRestoration = 'manual'; } catch (e) {}
  }

  var PRODUCT_SELECTOR = '.product-card, [data-product-handle], .product-grid__item';

  class LoadMore extends HTMLElement {
    constructor() {
      super();
      this.pagesLoaded = 0;
      this.isLoading = false;
    }

    connectedCallback() {
      this.containerId = this.dataset.container;
      this.itemsGridContainer = document.querySelector('#' + this.containerId);
      this.loadMoreBtn = this.querySelector('.js-load-more-btn');
      this.totalItemsViewed = this.querySelector('.js-total-items');
      if (!this.itemsGridContainer) return;

      // Section id of the listing section (set as data-id on the grid).
      this.sectionId = this.itemsGridContainer.dataset.id || '';

      // Total number of items in this listing, read from the total text
      // (e.g. "You seen 20 out of 1464 products") so we can show an
      // accurate running count and know when to stop.
      this.totalItems = this.readTotal();

      // Current page we are on. The first render is page 1.
      this.currentPage = this.readCurrentPage();

      // Are there more pages to load? Based on real counts.
      this.hasMore = this.countLoadedProducts() < this.totalItems;

      this.updateTotalText();
      this.bindEvents();
      this.setupScrollRestore();

      if (!this.hasMore) this.hideButton();
    }

    bindEvents() {
      if (this.loadMoreBtn) {
        this.loadMoreBtn.addEventListener('click', () => this.loadMoreItems());
      }
    }

    hideButton() {
      this.hasMore = false;
      if (this.loadMoreBtn) this.loadMoreBtn.style.display = 'none';
    }

    countLoadedProducts() {
      if (!this.itemsGridContainer) return 0;
      return this.itemsGridContainer.querySelectorAll(PRODUCT_SELECTOR).length;
    }

    // Parse the total ("... out of 1464 ...") from the count text.
    readTotal() {
      var txt = this.totalItemsViewed ? (this.totalItemsViewed.textContent || '') : '';
      var nums = txt.replace(/[^0-9 ]/g, ' ').trim().split(/\s+/).filter(Boolean);
      if (nums.length >= 2) {
        var t = parseInt(nums[nums.length - 1], 10);
        if (!isNaN(t)) return t;
      }
      // Fallback: a very large number so we keep loading until a page is empty.
      return Number.MAX_SAFE_INTEGER;
    }

    readCurrentPage() {
      var m = (window.location.search || '').match(/[?&]page=(\d+)/);
      return m ? parseInt(m[1], 10) : 1;
    }

    // Build a Section Rendering API URL for a given page.
    sectionUrl(page) {
      var path = window.location.pathname;
      var params = '?section_id=' + encodeURIComponent(this.sectionId) + '&page=' + page;
      // Preserve sort and filter params from the current URL so the loaded
      // pages match what the shopper is viewing.
      var current = window.location.search.replace(/^\?/, '');
      if (current) {
        current = current
          .split('&')
          .filter(function (p) {
            return p && p.indexOf('page=') !== 0 && p.indexOf('section_id=') !== 0;
          })
          .join('&');
        if (current) params += '&' + current;
      }
      return path + params;
    }

    // Update the "You seen X out of Y products" text from real counts.
    updateTotalText() {
      if (!this.totalItemsViewed) return;
      var seen = this.countLoadedProducts();
      var total = this.totalItems === Number.MAX_SAFE_INTEGER ? seen : this.totalItems;
      var template = this.totalItemsViewed.getAttribute('data-template');
      if (!template) {
        // Capture the original phrasing once, then swap the numbers.
        var original = this.totalItemsViewed.textContent || '';
        // Build a template by replacing the first two numbers with tokens.
        var count = 0;
        template = original.replace(/\d+/g, function (n) {
          count++;
          if (count === 1) return '{{seen}}';
          if (count === 2) return '{{total}}';
          return n;
        });
        // If we could not find two numbers, use a sensible default.
        if (template.indexOf('{{seen}}') === -1 || template.indexOf('{{total}}') === -1) {
          template = 'You seen {{seen}} out of {{total}} products';
        }
        this.totalItemsViewed.setAttribute('data-template', template);
      }
      this.totalItemsViewed.textContent = template
        .replace('{{seen}}', seen)
        .replace('{{total}}', total);
    }

    storageKey() {
      return 'loadmore:' + this.containerId + ':' + window.location.pathname + window.location.search;
    }

    setupScrollRestore() {
      window.addEventListener('pagehide', () => {
        try {
          sessionStorage.setItem(this.storageKey(), JSON.stringify({ pages: this.pagesLoaded || 0, y: window.scrollY }));
        } catch (e) {}
      });

      var raw = null;
      try { raw = sessionStorage.getItem(this.storageKey()); } catch (e) {}
      if (!raw) return;
      var saved;
      try { saved = JSON.parse(raw); } catch (e) { return; }
      if (!saved) return;
      if (!saved.pages || saved.pages < 1) {
        this.restoreScroll(saved.y);
        return;
      }
      this.restorePages(saved.pages, saved.y);
    }

    async restorePages(pageCount, y) {
      for (var i = 0; i < pageCount; i++) {
        if (!this.hasMore) break;
        // eslint-disable-next-line no-await-in-loop
        await this.loadMoreItems(true);
      }
      this.restoreScroll(y);
    }

    restoreScroll(y) {
      if (!y) return;
      requestAnimationFrame(() => {
        setTimeout(() => { window.scrollTo(0, y); }, 60);
      });
    }

    async loadMoreItems(silent) {
      if (!this.hasMore || this.isLoading) return;
      this.isLoading = true;
      if (this.loadMoreBtn) this.loadMoreBtn.classList.add('button--loading');

      var nextPage = this.currentPage + 1;
      var url = this.sectionUrl(nextPage);

      try {
        var res = await fetch(url);
        var html = await res.text();
        var doc = new DOMParser().parseFromString(html, 'text/html');

        // The Section Rendering API returns the section markup. Find the
        // grid container inside it.
        var newGrid = doc.querySelector('#' + this.containerId);
        var appended = 0;

        if (newGrid) {
          var cards = newGrid.querySelectorAll(PRODUCT_SELECTOR);
          appended = cards.length;
          if (appended > 0) {
            this.itemsGridContainer.insertAdjacentHTML('beforeend', newGrid.innerHTML);
            this.pagesLoaded += 1;
            this.currentPage = nextPage;
          }
        }

        if (window.lozadObserver) window.lozadObserver.observe();
        if (this.loadMoreBtn) this.loadMoreBtn.classList.remove('button--loading');

        var loaded = this.countLoadedProducts();
        if (appended === 0 || loaded >= this.totalItems) {
          this.updateTotalText();
          this.hideButton();
        } else {
          this.updateTotalText();
        }

        this.isLoading = false;

        if (!silent) {
          document.dispatchEvent(new CustomEvent('listing-load-more:loaded'));
        }
      } catch (err) {
        console.error(err);
        this.isLoading = false;
        if (this.loadMoreBtn) this.loadMoreBtn.classList.remove('button--loading');
      }
    }
  }

  if (!customElements.get('w3-load-more')) {
    customElements.define('w3-load-more', LoadMore);
  }
})();
